import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { 
    collection, 
    query, 
    getDocs, 
    doc, 
    orderBy, 
    onSnapshot, 
    setDoc,
    writeBatch,
    serverTimestamp 
} from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO PROFUNDO
import { deleteCobranzaFull } from '../../../../firebase/firebaseUtils';

import ModalCobranza from '../../../ui/ModalCobranza';
import ModalNotificacion from '../../../ui/ModalNotificacion';
import ModalConfirmacion from '../../../ui/ModalConfirmacion';
import { getCobranzaStatusChip, getPersonaChipClass } from '../../../../utils/styles';

// --- HELPERS DE FORMATO ---

/**
 * renderSafeValue - Soluciona el problema del "0". 
 * Si el valor es 0, lo muestra. Si es nulo/undefined/vacio, muestra "-".
 */
const renderSafeValue = (val) => {
    if (val === 0 || val === "0") return "0";
    if (val === undefined || val === null || val === "") return "-";
    return val;
};

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    // Caso: Timestamp de Firebase (objeto con seconds)
    if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        return new Date(dateValue.seconds * 1000).toLocaleDateString('es-AR');
    }
    // Caso: Objeto Date nativo
    if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString('es-AR');
    }
    // Caso: String (ISO o YYYY-MM-DD)
    if (typeof dateValue === 'string') {
        if (dateValue.includes('T')) {
            return new Date(dateValue).toLocaleDateString('es-AR');
        }
        const parts = dateValue.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateValue;
    }
    return '-';
};

// --- ICONOS ---
const IconCheck = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
const IconWallet = () => <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>;
const IconSkull = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h.01M15 13h.01M9 21h6M9 17h6m-7-5a3 3 0 013-3h4a3 3 0 013 3v2a3 3 0 01-3 3h-4a3 3 0 01-3-3v-2z" /></svg>;

/**
 * CobranzaTab - Gestión de deudas y pagos con actualización en tiempo real.
 */
const CobranzaTab = ({ poliza, client }) => {
    const [deudas, setDeudas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deudaTotal, setDeudaTotal] = useState({ amount: 0, currency: '$' });

    const [showModal, setShowModal] = useState(false);
    const [editingDeuda, setEditingDeuda] = useState(null);
    const [expandedDeudaId, setExpandedDeudaId] = useState(null);
    const [historialRechazos, setHistorialRechazos] = useState({});

    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, itemToDelete: null });
    
    // --- ESTADOS PARA INCOBRABLE ---
    const [incobrableConfirm, setIncobrableConfirm] = useState(false);
    const [isProcessingBatch, setIsProcessingBatch] = useState(false);

    const polizaId = poliza?.id;
    const clientId = client?.id;
    const compania = (poliza?.compania || '').toUpperCase();

    // --- LÓGICA DE VISIBILIDAD DE COLUMNAS SEGÚN COMPAÑÍA ---
    // SANCOR > Mostrar unicamente Recibo
    // LA SEGUNDA / ATM > Cuota
    // MERCANTIL > Endoso
    // DIGNA / RESTO > Endoso y cuota
    const showRecibo = compania === 'SANCOR';
    const showCuota = !['SANCOR', 'MERCANTIL'].includes(compania);
    const showEndoso = !['SANCOR', 'LA SEGUNDA', 'ATM'].includes(compania);

    // Formateador numérico sin símbolo de moneda (se concatena el campo .moneda)
    const formatNumber = (value) => {
        return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
    };

    // --- 1. LISTENER PRINCIPAL DE COBRANZAS (Real-time) ---
    useEffect(() => {
        if (!polizaId || !clientId) return;

        setLoading(true);
        const collectionRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/cobranza`);
        
        const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
            const rawData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // ORDENAMIENTO POR IDENTIFICADOR (Reciente arriba)
            const sortedData = [...rawData].sort((a, b) => {
                const valA = Number(a.cuota ?? a.nroRecibo ?? a.endoso ?? 0);
                const valB = Number(b.cuota ?? b.nroRecibo ?? b.endoso ?? 0);
                
                // Si los números son distintos, ordenamos por valor descendente
                if (valB !== valA) return valB - valA;

                // Si son iguales (ej: mismo endoso pero distinta fecha), desempatamos por fecha
                const getMs = (v) => {
                    const date = (v?.ultimaModificacion || v?.fechaCarga);
                    if (!date) return 0;
                    if (date.seconds) return date.seconds * 1000;
                    return new Date(date).getTime() || 0;
                };
                return getMs(b) - getMs(a);
            });

            setDeudas(sortedData);
            
            let total = 0;
            const estadosPendientes = ['PENDIENTE', 'INFORMADA', 'SIN ASIGNAR', 'CBU READHERIDO', 'TC READHERIDA', 'INCOBRABLE', 'RECLAMADA'];
            sortedData.forEach(d => {
                if (d.estado && estadosPendientes.includes(d.estado.toUpperCase())) {
                    total += (d.importe || 0);
                }
            });
            setDeudaTotal({ amount: total, currency: poliza?.moneda || '$' });
            setLoading(false);
        }, (err) => {
            console.error("Error en Snapshot CobranzaTab:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [polizaId, clientId, poliza?.moneda]);

    // --- 2. LISTENER DE OBSERVACIONES ---
    useEffect(() => {
        if (!expandedDeudaId || !polizaId || !clientId) return;

        const ref = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/cobranza/${expandedDeudaId}/observaciones`);
        const q = query(ref, orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setHistorialRechazos(prev => ({ 
                ...prev, 
                [expandedDeudaId]: snapshot.docs.map(d => d.data()) 
            }));
        }, (err) => {
            console.error("Error en listener de observaciones:", err);
        });

        return () => unsubscribe();
    }, [expandedDeudaId, polizaId, clientId]);

    const handleUpdateGlobalStatus = async () => {
        try {
            const colRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/cobranza`);
            const snap = await getDocs(colRef);
            const estadosDeudaActiva = ['PENDIENTE', 'NUEVO INTENTO', 'INFORMADA', 'DEUDA', 'INCOBRABLE', 'RECLAMADA'];
            
            const tieneDeudaReal = snap.docs.some(doc => {
                const d = doc.data();
                return d.estado && estadosDeudaActiva.includes(d.estado.toUpperCase());
            });

            await setDoc(doc(db, `public/data/clientes/${clientId}/polizas`, polizaId), { 
                tieneDeuda: tieneDeudaReal,
                ultimaModificacion: serverTimestamp()
            }, { merge: true });
        } catch (e) { 
            console.error("Error actualizando status global de deuda:", e); 
        }
    };

    const handleMarcarTodoIncobrable = async () => {
        if (deudas.length === 0) return;
        setIsProcessingBatch(true);
        setIncobrableConfirm(false);
        try {
            const batch = writeBatch(db);
            const baseRef = `public/data/clientes/${clientId}/polizas/${polizaId}/cobranza`;
            
            deudas.forEach(item => {
                const estadoActual = (item.estado || '').toUpperCase();
                if (!['COBRADA', 'ANULADA', 'DEBITADA', 'ACREDITADA'].includes(estadoActual)) {
                    const docRef = doc(db, baseRef, item.id);
                    batch.set(docRef, { 
                        estado: 'Incobrable', 
                        ultimaModificacion: serverTimestamp() 
                    }, { merge: true });
                }
            });
            
            await batch.commit();
            await handleUpdateGlobalStatus();
            setNotification({ show: true, message: 'Todas las cuotas pendientes pasaron a estado Incobrable.', type: 'success' });
        } catch (error) {
            setNotification({ show: true, message: 'Error al procesar la actualización masiva.', type: 'error' });
        } finally {
            setIsProcessingBatch(false);
        }
    };

    const handleDelete = async () => {
        const id = deletionConfirm.itemToDelete.id;
        setDeletionConfirm({ show: false, itemToDelete: null });
        try {
            await deleteCobranzaFull(`public/data/clientes/${clientId}/polizas/${polizaId}`, id);
            await handleUpdateGlobalStatus();
            setNotification({ show: true, message: 'Registro eliminado correctamente.', type: 'success' });
        } catch (e) { 
            setNotification({ show: true, message: 'Error al intentar eliminar el registro.', type: 'error' }); 
        }
    };

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
            
            {/* INDICADOR DEUDA */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500"></div>
                <div>
                    <p className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-1">Deuda Total Pendiente</p>
                    <div className="flex items-baseline gap-2 text-left">
                        <span className="text-3xl font-black text-slate-900 tracking-tighter">
                            {poliza?.moneda || '$'} {formatNumber(deudaTotal.amount)}
                        </span>
                    </div>
                </div>
                
                <div className="flex items-center gap-3">
                    {deudas.length > 0 && (
                        <button 
                            onClick={() => setIncobrableConfirm(true)} 
                            disabled={isProcessingBatch}
                            className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl font-bold text-[10px] border border-red-100 hover:bg-red-600 hover:text-white transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        >
                            <IconSkull />
                            Póliza Incobrable
                        </button>
                    )}

                    <button 
                        onClick={() => { setEditingDeuda(null); setShowModal(true); }} 
                        className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                        Cargar Cuota
                    </button>
                </div>
            </div>

            {/* TABLA DE COBRANZAS */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1300px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                        <tr>
                            <th className="p-4 w-12 text-center"></th>
                            <th className="p-4 w-28 whitespace-nowrap">F. Gestión</th>
                            {showEndoso && <th className="p-4 w-20 text-center">Endoso</th>}
                            {showCuota && <th className="p-4 w-20 text-center">Cuota</th>}
                            {showRecibo && <th className="p-4 w-24 text-center">Recibo</th>}
                            <th className="p-4 w-40 text-right">Importe</th>
                            <th className="p-4 w-32 text-center">Tipo Gestión</th>
                            <th className="p-4 w-40 text-center">Estado de Deuda</th>
                            <th className="p-4 w-12 text-center">Int.</th>
                            <th className="p-4 w-36 text-center">Forma de Pago</th>
                            <th className="p-4 w-28 text-center whitespace-nowrap">F. Reclamo</th>
                            <th className="p-4 w-32 text-center">Responsable</th>
                            <th className="p-4 w-32 text-center">Quién Cobró</th>
                            <th className="p-4 w-28 text-center whitespace-nowrap">F. Cobro</th>
                            <th className="p-4 w-20 text-center">Reher.</th>
                            <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="17" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando cobranzas...</td></tr>
                        ) : deudas.length === 0 ? (
                            <tr><td colSpan="17" className="p-16 text-center text-slate-400 italic font-medium">Sin movimientos registrados.</td></tr>
                        ) : deudas.map((d) => (
                            <React.Fragment key={d.id}>
                                <tr onClick={() => setExpandedDeudaId(expandedDeudaId === d.id ? null : d.id)} className={`hover:bg-slate-50 transition-colors cursor-pointer group ${expandedDeudaId === d.id ? 'bg-blue-50/40' : ''}`}>
                                    <td className="p-4 text-center">
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${expandedDeudaId === d.id ? 'rotate-90 text-blue-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                                    </td>
                                    <td className="p-4 font-bold text-slate-500 tabular-nums whitespace-nowrap">{formatDate(d.ultimaModificacion || d.fechaCarga)}</td>
                                    
                                    {/* VISIBILIDAD POR COMPAÑÍA Y MANEJO DE 0 */}
                                    {showEndoso && <td className="p-4 text-center font-bold text-slate-800">{renderSafeValue(d.endoso)}</td>}
                                    {showCuota && <td className="p-4 text-center font-bold text-slate-800">{renderSafeValue(d.cuota)}</td>}
                                    {showRecibo && <td className="p-4 text-center font-bold text-slate-800">{renderSafeValue(d.nroRecibo)}</td>}
                                    
                                    <td className="p-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {d.hasSaldoAFavor && (
                                                <span className={`flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border whitespace-nowrap ${d.saldoAFavorEstado === 'Aplicado' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-indigo-50 text-indigo-600 border-indigo-200'}`}>
                                                    <IconWallet /> SALDO {d.saldoAFavorEstado === 'Aplicado' ? 'APLIC.' : 'PEND.'}
                                                </span>
                                            )}
                                            {/* MONEDA DINÁMICA SEGÚN REGISTRO */}
                                            <span className="font-black text-blue-700 whitespace-nowrap">
                                                {d.moneda || '$'} {formatNumber(d.importe)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-center whitespace-nowrap">
                                        <span className={`${getCobranzaStatusChip(d.estado)} whitespace-nowrap`}>{d.estado}</span>
                                    </td>
                                    <td className="p-4 text-center whitespace-nowrap">
                                        <span className={`${getCobranzaStatusChip(d.estadoDeuda)} whitespace-nowrap`}>{d.estadoDeuda || 'S/D'}</span>
                                    </td>
                                    <td className="p-4 text-center font-bold text-slate-400 tabular-nums">{renderSafeValue(d.intentoCobro)}</td>
                                    <td className="p-4 text-center whitespace-nowrap text-left">
                                        <span className="bg-blue-50 border border-blue-100 rounded-lg text-[11px] text-blue-800 font-bold uppercase gap-2 px-3 py-1">{d.formaPago || '-'}</span>
                                    </td>
                                    <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">{formatDate(d.fechaReclamo)}</td>
                                    <td className="p-4 text-center whitespace-nowrap">{d.quienInformo || '-'}</td>
                                    <td className="p-4 text-center whitespace-nowrap">
                                        {d.quienCobro ? (<div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(d.quienCobro)}`}>{d.quienCobro}</div>) : '-'}
                                    </td>
                                    <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">{formatDate(d.fechaCobro)}</td>
                                    <td className="p-4 text-center">
                                        {d.medioPagoReherido ? (<div className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-sm" title="Reherido"><IconCheck /></div>) : <span className="text-slate-200">-</span>}
                                    </td>
                                    <td className={`p-4 text-right pr-6 sticky right-0 z-10 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)] ${expandedDeudaId === d.id ? 'bg-[#f8faff]' : 'bg-white group-hover:bg-slate-50'}`}>
                                        <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                            <button onClick={(e) => { e.stopPropagation(); setEditingDeuda(d); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                            <button onClick={(e) => { e.stopPropagation(); setDeletionConfirm({ show: true, itemToDelete: d }); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                        </div>
                                    </td>
                                </tr>
                                {expandedDeudaId === d.id && (
                                    <tr className="bg-slate-50/50 border-b border-slate-100 animate-fade-in">
                                        <td colSpan="17" className="px-12 py-6 text-left">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                <div className="space-y-4">
                                                    {d.medioPagoReherido && (
                                                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm text-left">
                                                            <h4 className="text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-3 flex items-center gap-2"><IconCheck /> Detalles de Readhesión</h4>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div><p className="text-[9px] text-emerald-600 uppercase font-bold">Forma de Pago</p><p className="text-sm font-black text-emerald-900 uppercase">{d.formaPagoCobro || '-'}</p></div>
                                                                <div><p className="text-[9px] text-emerald-600 uppercase font-bold">Medio Utilizado</p><p className="text-sm font-black text-emerald-900">{d.medioPago || 'S/D'}</p></div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {d.hasSaldoAFavor && (
                                                        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 shadow-sm text-left">
                                                            <h4 className="text-[10px] font-black text-indigo-700 uppercase tracking-widest mb-3 flex items-center gap-2"><IconWallet /> Saldo a Favor Generado</h4>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div><p className="text-[9px] text-indigo-600 uppercase font-bold">Importe</p><p className="text-lg font-black text-indigo-900">{d.moneda || '$'} {formatNumber(d.saldoAFavorImporte)}</p></div>
                                                                <div>
                                                                    <p className="text-[9px] text-indigo-600 uppercase font-bold">Estado</p>
                                                                    <span className={`px-3 py-1 rounded text-[10px] font-black uppercase border inline-block ${d.saldoAFavorEstado === 'Aplicado' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                                                                        {d.saldoAFavorEstado || 'PENDIENTE'}
                                                                    </span>
                                                                </div>
                                                                <div className="col-span-2 border-t border-indigo-100 pt-2 flex justify-between items-center text-left">
                                                                    <div><p className="text-[9px] text-indigo-600 uppercase font-bold">Referencia</p><p className="text-xs font-bold text-indigo-800 italic">"{d.saldoAFavorReferencia || 'Sin referencia'}"</p></div>
                                                                    {d.saldoAFavorEstado === 'Aplicado' && (
                                                                        <div className="text-right"><p className="text-[9px] text-emerald-600 uppercase font-bold">Vínculo</p><p className="text-xs font-black text-emerald-700 uppercase">Aplicado a: {d.saldoAFavorCuotaAplicada || 'N/A'}</p></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 shadow-sm w-fit text-left">
                                                        <span className="font-bold text-slate-400 uppercase text-[10px] tracking-wide">Motivo Deuda:</span>
                                                        <span className="text-slate-700 font-bold text-sm italic">"{d.motivoDeuda || 'No especificado'}"</span>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col gap-2 text-left">
                                                    <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2 mb-1">Historial de Observaciones</h4>
                                                    <div className="pl-4 border-l-4 border-blue-100 space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                                                        {historialRechazos[d.id]?.length > 0 ? historialRechazos[d.id].map((h, idx) => (
                                                            <div key={idx} className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm text-gray-700 text-sm flex gap-3 items-start animate-fade-in">
                                                                <span className="font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-[10px] uppercase shrink-0 mt-0.5">{formatDate(h.fechaCreacion)}</span>
                                                                <span className="text-slate-600 font-medium leading-tight">{h.comentario || 'Sin comentario'}</span>
                                                            </div>
                                                        )) : <p className="text-slate-300 italic text-xs p-2 text-left">Sin historial registrado.</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>

            {/* MODALES */}
            <ModalCobranza 
                show={showModal} 
                onClose={() => setShowModal(false)} 
                poliza={poliza} 
                clientData={client} 
                initialData={editingDeuda} 
                onSuccess={async () => { 
                    setShowModal(false); 
                    await handleUpdateGlobalStatus(); 
                    setNotification({show: true, message: 'La información se ha actualizado correctamente.', type: 'success'}); 
                }} 
            />
            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />
            <ModalConfirmacion show={deletionConfirm.show} onClose={() => setDeletionConfirm({ show: false, itemToDelete: null })} onConfirm={handleDelete} title="Eliminar Registro" message="¿Estás seguro de que deseas eliminar este registro de cobranza? Esta acción es irreversible." />
            
            {/* CONFIRMACIÓN PARA INCOBRABLE */}
            <ModalConfirmacion 
                show={incobrableConfirm} 
                onClose={() => setIncobrableConfirm(false)} 
                onConfirm={handleMarcarTodoIncobrable} 
                title="Póliza Incobrable" 
                message="¿Desea marcar todas las cuotas pendientes como Incobrable? Esta acción es masiva y afectará a todos los registros abiertos." 
            />
        </div>
    );
};

export default CobranzaTab;